// Phase 1: Enhanced User Types and Role System
// File: src/types/RoleSystem.ts
export type UserRole = 'Super Admin' | 'Admin' | 'Manager' | 'Agent';

export interface Permission {
  id: string;
  name: string;
  description: string;
  category: 'document' | 'user' | 'agency' | 'system' | 'education';
}

export interface RoleDefinition {
  role: UserRole;
  permissions: Permission[];
  accessLevel: number; // 1 = highest (Super Admin), 4 = lowest (Agent)
  inheritsFrom?: UserRole; // Role inheritance hierarchy
  description: string;
}

// Permission system
export const PERMISSIONS = {
  // Document Management
  DOCUMENT_VIEW_ALL_USERS: { id: 'doc_view_all', name: 'View All User Documents', description: 'Can view documents of all users in agency', category: 'document' as const },
  DOCUMENT_EDIT_ALL_USERS: { id: 'doc_edit_all', name: 'Edit All User Documents', description: 'Can edit documents of all users in agency', category: 'document' as const },
  DOCUMENT_MANAGE_COMPANY: { id: 'doc_company', name: 'Manage Company Documents', description: 'Can manage agency-wide company documents', category: 'document' as const },
  DOCUMENT_VIEW_OWN: { id: 'doc_view_own', name: 'View Own Documents', description: 'Can view own documents', category: 'document' as const },
  DOCUMENT_EDIT_OWN: { id: 'doc_edit_own', name: 'Edit Own Documents', description: 'Can edit own documents', category: 'document' as const },
  
  // User Management
  USER_INVITE: { id: 'user_invite', name: 'Invite Users', description: 'Can invite new users to agency', category: 'user' as const },
  USER_MANAGE_AGENCY: { id: 'user_manage_agency', name: 'Manage Agency Users', description: 'Can manage users within agency', category: 'user' as const },
  USER_MANAGE_SYSTEM: { id: 'user_manage_system', name: 'Manage System Users', description: 'Can manage users across all agencies', category: 'user' as const },
  
  // Agency Management
  AGENCY_MANAGE_OWN: { id: 'agency_manage_own', name: 'Manage Own Agency', description: 'Can manage own agency settings', category: 'agency' as const },
  AGENCY_MANAGE_ALL: { id: 'agency_manage_all', name: 'Manage All Agencies', description: 'Can manage all agencies in system', category: 'agency' as const },
  
  // System Administration
  SYSTEM_ADMIN: { id: 'system_admin', name: 'System Administration', description: 'Full system administration access', category: 'system' as const },
  SYSTEM_SETTINGS: { id: 'system_settings', name: 'System Settings', description: 'Can modify system-wide settings', category: 'system' as const },
  
  // Education Hub
  EDUCATION_ADMIN: { id: 'education_admin', name: 'Education Administration', description: 'Can manage all educational content', category: 'education' as const },
  EDUCATION_VIEW: { id: 'education_view', name: 'View Education Content', description: 'Can view educational content', category: 'education' as const },
} as const;

// Role Definitions with Inheritance
export const ROLE_DEFINITIONS: Record<UserRole, RoleDefinition> = {
  'Super Admin': {
    role: 'Super Admin',
    accessLevel: 1,
    description: 'Product owners with full system access across all agencies and platform management',
    permissions: Object.values(PERMISSIONS), // All permissions
  },
  'Admin': {
    role: 'Admin',
    accessLevel: 2,
    inheritsFrom: 'Manager',
    description: 'Agency administrators with full agency management and cross-user oversight within their agency',
    permissions: [
      PERMISSIONS.DOCUMENT_VIEW_ALL_USERS,
      PERMISSIONS.DOCUMENT_EDIT_ALL_USERS,
      PERMISSIONS.DOCUMENT_MANAGE_COMPANY,
      PERMISSIONS.USER_INVITE,
      PERMISSIONS.USER_MANAGE_AGENCY,
      PERMISSIONS.AGENCY_MANAGE_OWN,
      PERMISSIONS.EDUCATION_VIEW,
      // Can view own documents as well (inherited from Manager/Agent)
    ],
  },
  'Manager': {
    role: 'Manager',
    accessLevel: 3,
    inheritsFrom: 'Agent',
    description: 'Agency managers with user oversight and company document management',
    permissions: [
      PERMISSIONS.DOCUMENT_VIEW_ALL_USERS,
      PERMISSIONS.DOCUMENT_EDIT_ALL_USERS,
      PERMISSIONS.DOCUMENT_MANAGE_COMPANY,
      PERMISSIONS.USER_INVITE,
      PERMISSIONS.USER_MANAGE_AGENCY,
      PERMISSIONS.AGENCY_MANAGE_OWN,
      PERMISSIONS.EDUCATION_VIEW,
      // Inherits Agent permissions (own document access)
    ],
  },
  'Agent': {
    role: 'Agent',
    accessLevel: 4,
    description: 'Individual agents with personal document access and basic education access',
    permissions: [
      PERMISSIONS.DOCUMENT_VIEW_OWN,
      PERMISSIONS.DOCUMENT_EDIT_OWN,
      PERMISSIONS.EDUCATION_VIEW,
    ],
  },
};

// Helper Functions
export function hasPermission(userRole: UserRole, permissionId: string): boolean {
  const roleDefn = ROLE_DEFINITIONS[userRole];
  return roleDefn.permissions.some(p => p.id === permissionId);
}

export function canAccessFeature(userRole: UserRole, requiredPermissions: string[]): boolean {
  return requiredPermissions.every(permission => hasPermission(userRole, permission));
}

export function getRoleHierarchy(role: UserRole): UserRole[] {
  const hierarchy: UserRole[] = [role];
  let currentRole = ROLE_DEFINITIONS[role];
  
  while (currentRole.inheritsFrom) {
    hierarchy.push(currentRole.inheritsFrom);
    currentRole = ROLE_DEFINITIONS[currentRole.inheritsFrom];
  }
  
  return hierarchy;
}
